import React, { useEffect, useRef, useState } from "react";
import { Box, keyframes, Typography, useTheme } from "@mui/material";
import { Layout } from "src/components/common";
import { useTranslation } from "react-i18next";
import { Bubbles, Circle } from "../reasons";

const gradient = keyframes`
    0% {
    background-position: 0 50%;
}
    25% {
    background-position: 50% 0;
}
    50% {
    background-position: 50% 100%;
}
    75% {
    background-position: 75% 0;
}
    100% {
    background-position: 0 50%;
}
`;

const textStyle = {
  textTransform: "uppercase",
  letterSpacing: "-0.1px",
  position: "relative",
  zIndex: 6,
  animation: `${gradient} 15s infinite`,
  backgroundSize: "400% 400%",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundImage: "radial-gradient(circle,#adf6bd 0,#357bf0 40%)",
  lineHeight: 1.2,
  color: "white",
  paddingInline: "90px",
  whiteSpace: "pre-wrap",
  fontSize: "66px",
  fontFamily: "ZenOldMincho",
};

type ContentType = {
  title: string;
  content: string;
};

export const ReasonsPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const scrollYRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [boxScrollProgress, setBoxScrollProgress] = useState(0);
  const vh = window.innerHeight;

  const contentList: ContentType[] = [
    { title: t("reasons.title1"), content: t("reasons.content1") },
    { title: t("reasons.title2"), content: t("reasons.content2") },
  ];

  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      scrollYRef.current += event.deltaY;
      const y = scrollYRef.current;

      if (y <= 0) {
        setProgress(0);
      } else if (y < 1 * vh) {
        const progressValue = Math.min(y / vh, 1);
        setProgress(progressValue);
      } else if (y < 2 * vh) {
        setProgress(1);
      }

      if (y >= 2 * vh && y < 3 * vh) {
        const progressValue = Math.min((y - 2 * vh) / vh, 1);
        setBoxScrollProgress(progressValue + 1);
      } else if (y >= vh && y < 2 * vh) {
        const progressValue = Math.min((y - vh) / vh, 1);
        setBoxScrollProgress(progressValue);
      } else if (y < vh) {
        setBoxScrollProgress(0);
      } else {
        setBoxScrollProgress(2);
      }
    };

    window.addEventListener("wheel", onWheel);
    return () => window.removeEventListener("wheel", onWheel);
  }, [vh]);

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
          width: "300vw",
          maxWidth: "300vw",
          background: "linear-gradient(180deg, #000000 0%, #230D4D 100%)",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            minHeight: "100vh",
            width: "100vw",
            minWidth: "100vw",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            transform: `translateX(-${boxScrollProgress * 100}vw)`,
          }}
        >
          <Circle progress={progress} />
          <Typography sx={{ ...textStyle, fontSize: "96px", lineHeight: 1.5, [theme.breakpoints.down("md")]: { fontSize: "52px" } }}>
            {t("reasons.title")}
          </Typography>
          <Typography
            sx={{
              mt: "10px",
              fontSize: "28px",
              fontFamily: "ZenOldMincho",
              color: "white",
              [theme.breakpoints.down("md")]: { fontSize: "20px" },
            }}
          >
            {t("reasons.description")}
          </Typography>
        </Box>
        {contentList.map(item => (
          <Box
            key={item.title}
            sx={{
              width: "100vw",
              minWidth: "100vw",
              height: "100vh",
              textAlign: "center",
              transform: `translateX(${-boxScrollProgress * 100}vw)`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ ...textStyle, [theme.breakpoints.down("md")]: { fontSize: "32px", paddingInline: "20px" } }}>
              {item.title}
            </Typography>
            <Typography
              sx={{
                mt: "30px",
                lineHeight: 2,
                whiteSpace: "pre-wrap",
                paddingInline: "90px",
                color: "white",
                fontSize: "28px",
                [theme.breakpoints.down("md")]: { paddingInline: "20px", fontSize: "16px" },
              }}
            >
              {item.content}
            </Typography>
          </Box>
        ))}
        <Box sx={{ position: "absolute", width: "100%", height: "100%", left: 0, top: 0 }}>
          <Bubbles />
        </Box>
      </Box>
    </Layout>
  );
};

export default ReasonsPage;
